import { type FC } from 'react';
import { cn } from '@/utils/cn';

export type ShotName =
  | 'home' | 'hobby' | 'collection' | 'unit' | 'journal' | 'project' | 'paint-next'
  | 'grimm' | 'log-result' | 'results' | 'near-me' | 'club' | 'events';
export type ShotKind = 'desktop' | 'phone';

/** Intrinsic sizes of the largest export of each kind; the smaller one is half as wide at the same ratio. */
const DIMS: Record<ShotKind, { width: number; height: number; widths: number[] }> = {
  desktop: { width: 1200, height: 750, widths: [720, 1200] },
  phone: { width: 780, height: 1688, widths: [390, 520, 780] },
};

const src = (name: ShotName, kind: ShotKind, width: number, format: 'avif' | 'webp') =>
  `/images/marketing/${name}-${kind}-${width}.${format}`;

interface LandingShotProps {
  name: ShotName;
  kind: ShotKind;
  /** What the screenshot shows, for people who cannot see it. */
  alt: string;
  /** Hero shots load straight away; everything below the fold is lazy. */
  eager?: boolean;
  /** The width the picture takes on the page, as a CSS `sizes` value. */
  sizes: string;
  className?: string;
}

/**
 * LandingShot - one real screenshot of the app as AVIF with a WebP fallback, in two widths.
 * Width and height are set so nothing shifts while it loads.
 */
export const LandingShot: FC<LandingShotProps> = ({ name, kind, alt, eager = false, sizes, className }) => {
  const { width, height, widths } = DIMS[kind];
  const set = (format: 'avif' | 'webp') => widths.map((w) => `${src(name, kind, w, format)} ${w}w`).join(', ');
  return (
    <picture>
      <source type="image/avif" srcSet={set('avif')} sizes={sizes} />
      <source type="image/webp" srcSet={set('webp')} sizes={sizes} />
      <img
        src={src(name, kind, widths[widths.length - 1], 'webp')}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? 'eager' : 'lazy'}
        decoding={eager ? 'sync' : 'async'}
        {...(eager ? { fetchPriority: 'high' as const } : {})}
        className={cn('block h-auto w-full', className)}
      />
    </picture>
  );
};

interface ShotPairProps {
  desktop: { name: ShotName; alt: string };
  phone: { name: ShotName; alt: string };
  eager?: boolean;
  /** Put the phone on the left of the desktop frame instead of the right. */
  phoneLeft?: boolean;
}

/**
 * ShotPair - a desktop browser frame with a phone overlapping its corner. On narrow screens only the phone
 * shows, so a phone never downloads the large desktop picture.
 */
export const ShotPair: FC<ShotPairProps> = ({ desktop, phone, eager = false, phoneLeft = false }) => (
  <div className="relative mx-auto w-full max-w-2xl sm:mb-10">
    <div className="hidden overflow-hidden rounded-lg border border-border-subtle bg-bg-secondary shadow-lg sm:block">
      <div aria-hidden="true" className="flex gap-1.5 border-b border-border-subtle bg-bg-tertiary px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-border-default" />
        <span className="h-2.5 w-2.5 rounded-full bg-border-default" />
        <span className="h-2.5 w-2.5 rounded-full bg-border-default" />
      </div>
      <LandingShot name={desktop.name} kind="desktop" alt={desktop.alt} eager={eager} sizes="(min-width: 1024px) 640px, 90vw" />
    </div>
    <div
      className={cn(
        'mx-auto w-56 overflow-hidden rounded-[1.75rem] border-4 border-bg-tertiary bg-bg-secondary shadow-xl sm:absolute sm:-bottom-8 sm:w-40 lg:w-44',
        phoneLeft ? 'sm:-left-6' : 'sm:-right-6',
      )}
    >
      <LandingShot name={phone.name} kind="phone" alt={phone.alt} eager={eager} sizes="(min-width: 640px) 176px, 224px" />
    </div>
  </div>
);
